import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Activity, ArrowRight, CircleAlert, RefreshCw, Route as RouteIcon, Search } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { useAuth } from "@/hooks/useAuth";
import { dateLocale, t } from "@/lib/i18n";
import type { LogEntry, LogResponse } from "@/lib/types";

type TraceSummary = {
  traceId: string;
  count: number;
  services: string[];
  errorCount: number;
  warnCount: number;
  startTs: string;
  endTs: string;
  durationMs: number;
  topMessage: string;
};

const LOOKBACK_LIMIT = 500;

function summarize(logs: LogEntry[]): TraceSummary[] {
  const grouped = new Map<string, LogEntry[]>();
  for (const log of logs) {
    if (!log.trace_id) continue;
    const arr = grouped.get(log.trace_id);
    if (arr) arr.push(log);
    else grouped.set(log.trace_id, [log]);
  }
  const out: TraceSummary[] = [];
  for (const [traceId, entries] of grouped) {
    entries.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    const t0 = new Date(entries[0].timestamp).getTime();
    const tN = new Date(entries[entries.length - 1].timestamp).getTime();
    const services = Array.from(
      new Set(entries.map((e) => e.service ?? e.client_id).filter(Boolean) as string[]),
    );
    const errs = entries.filter((e) => e.level === "ERROR" || e.level === "FATAL");
    const warns = entries.filter((e) => e.level === "WARN");
    const topMessage = (errs[0] ?? entries[entries.length - 1]).message;
    out.push({
      traceId,
      count: entries.length,
      services,
      errorCount: errs.length,
      warnCount: warns.length,
      startTs: entries[0].timestamp,
      endTs: entries[entries.length - 1].timestamp,
      durationMs: tN - t0,
      topMessage,
    });
  }
  return out.sort((a, b) => new Date(b.endTs).getTime() - new Date(a.endTs).getTime());
}

export function TraceListPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [traces, setTraces] = useState<TraceSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const reload = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<LogResponse>(
        `/api/logs?limit=${LOOKBACK_LIMIT}&include_total=false`,
      );
      setTraces(summarize(resp.logs));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = (traces ?? []).filter(
    (tr) =>
      !search ||
      tr.traceId.includes(search) ||
      tr.topMessage.toLowerCase().includes(search.toLowerCase()) ||
      tr.services.some((s) => s.toLowerCase().includes(search.toLowerCase())),
  );

  const onJumpById = (e: React.FormEvent) => {
    e.preventDefault();
    const id = search.trim();
    if (id) navigate(`/traces/${encodeURIComponent(id)}`);
  };

  return (
    <div>
      <PageHeader
        title={t("Traces")}
        description={t("Trace-IDs aus den letzten {n} Logs, aggregiert nach trace_id.", { n: LOOKBACK_LIMIT })}
        actions={
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs hover:bg-muted disabled:opacity-60"
          >
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            {t("Aktualisieren")}
          </button>
        }
      />

      <div className="flex flex-col gap-4 p-6">
        <form onSubmit={onJumpById} className="flex items-center gap-2 rounded-md border border-border bg-card p-2">
          <Search className="ml-2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Trace-ID oder Nachricht — Enter springt direkt zur Timeline")}
            className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          {search ? (
            <button
              type="submit"
              className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
            >
              {t("Öffnen")} <ArrowRight className="h-3 w-3" />
            </button>
          ) : null}
        </form>

        {error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}

        <Card title={t("Letzte Traces ({n})", { n: filtered.length })}>
          {traces == null ? (
            <div className="text-sm text-muted-foreground">{t("Lade…")}</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              {t("Keine Logs mit")} <span className="font-mono">trace_id</span>{" "}
              {t("in den letzten {n} Einträgen.", { n: LOOKBACK_LIMIT })}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((tr) => (
                <li key={tr.traceId} className="flex items-center hover:bg-muted/40">
                  <Link
                    to={`/traces/${encodeURIComponent(tr.traceId)}`}
                    className="grid min-w-0 flex-1 grid-cols-[1fr_max-content] items-center gap-3 px-2 py-2.5"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <RouteIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate font-mono text-xs">{tr.traceId}</span>
                        {tr.errorCount > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-medium text-destructive">
                            <CircleAlert className="h-2.5 w-2.5" /> {tr.errorCount}
                          </span>
                        ) : null}
                        {tr.warnCount > 0 ? (
                          <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium text-warning">
                            {t("{n} warn", { n: tr.warnCount })}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {tr.topMessage}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                        <span>{tr.count === 1 ? t("1 Eintrag") : t("{n} Einträge", { n: tr.count })}</span>
                        {tr.services.length > 0 ? <span>· {tr.services.join(", ")}</span> : null}
                        <span>· {formatDuration(tr.durationMs)}</span>
                        <span>· {new Date(tr.endTs).toLocaleTimeString(dateLocale())}</span>
                      </div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </Link>
                  {/* Störungen (≥1 ERROR) direkt ins Zeitstrahl-Replay —
                      eigener Link NEBEN dem Zeilen-Link (kein a-in-a). */}
                  {tr.errorCount > 0 ? (
                    <Link
                      to={`/zeitstrahl/${encodeURIComponent(tr.traceId)}`}
                      title={t("Im Zeitstrahl öffnen")}
                      aria-label={t("Im Zeitstrahl öffnen")}
                      className="mr-2 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      <Activity className="h-4 w-4" />
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}
